import { 
  Building2, 
  User, 
  MapPin, 
  Mail, 
  Phone, 
  Globe, 
  Calendar, 
  FileText, 
  Tag,
  Check,
  X
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getFormattedDateTime } from "@/utils/settings";
import { useSettings } from "@/providers/SettingsProvider";
import { formatWebsiteUrl, getDisplayUrl } from "@/utils/urlUtils";
import { CompanyRelationship } from "@/utils/companyRelationship";
import { CompanyRelationshipBadge } from "./CompanyRelationshipBadge";

interface CompanyOverviewCardProps {
  company: {
    id: string;
    name: string;
    street?: string | null;
    postal_code?: string | null;
    city?: string | null;
    country?: string | null;
    address?: string | null;
    assignee?: string | null;
    notes?: string | null;
    logo_url?: string | null;
    vat_number?: string | null;
    website_url?: string | null;
    relationship?: CompanyRelationship | null;
    created_at?: string;
    custom_fields?: Record<string, any>;
  };
  customFieldsConfig?: any[]; // Custom fields configuration for the organization
}

export const CompanyOverviewCard = ({ company, customFieldsConfig = [] }: CompanyOverviewCardProps) => {
  const { t } = useTranslation();
  const { dateFormat, timeFormat } = useSettings();
  const [assigneeName, setAssigneeName] = useState<string>("");

  useEffect(() => {
    const fetchAssigneeName = async () => {
      if (company.assignee) {
        // Use the new database function that provides full access to user names
        const { data, error } = await supabase
          .rpc('get_user_display_info', {
            p_user_id: company.assignee
          });
        
        if (!error && data && data.length > 0) {
          const userInfo = data[0];
          setAssigneeName(`${userInfo.first_name || ""} ${userInfo.last_name || ""}`.trim());
        } else {
          setAssigneeName("");
        }
      } else {
        setAssigneeName("");
      }
    };
    fetchAssigneeName();
  }, [company.assignee]);

  // Helper function to check if a section has data to display
  const hasData = (values: (string | string[] | undefined | null)[]) => {
    return values.some(value => {
      if (Array.isArray(value)) return value && value.length > 0;
      return !!value;
    });
  };

  // Helper to check if custom fields have values (only for currently configured fields)
  const hasCustomFields = (fields?: Record<string, any>) => {
    if (!fields || !customFieldsConfig.length) return false;
    
    // Only check fields that are currently configured in the admin panel
    return customFieldsConfig.some(fieldConfig => {
      const fieldValue = fields[fieldConfig.name];
      if (fieldConfig.type === 'boolean') {
        // For boolean fields, always show if they have a value (true or false)
        return fieldValue !== undefined;
      }
      // For other fields, check if they have a non-empty value
      if (Array.isArray(fieldValue)) return fieldValue && fieldValue.length > 0;
      return !!fieldValue;
    });
  };

  // Format dates according to user settings
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return getFormattedDateTime(new Date(dateString), dateFormat, timeFormat, false);
  };

  // Helper to format custom field values
  const formatCustomFieldValue = (fieldName: string, fieldValue: any) => {
    // Check if this is a boolean field by looking at the custom fields configuration
    const fieldConfig = customFieldsConfig.find(field => field.name === fieldName);
    const isBooleanField = fieldConfig?.type === 'boolean';
    
    if (isBooleanField) {
      return fieldValue ? (
        <Check className="h-4 w-4 text-green-600" />
      ) : (
        <X className="h-4 w-4 text-red-600" />
      );
    }
    
    // For non-boolean fields, return the value as before
    return Array.isArray(fieldValue) ? fieldValue.join(', ') : String(fieldValue);
  };

  // Check which sections have data
  const hasContactInfo = hasData([company.vat_number]);
  const hasLocation = hasData([company.street, company.city, company.postal_code, company.country]);
  const hasDetails = hasData([company.street, company.city, company.postal_code, company.country, company.vat_number, company.website_url]);
  const hasGeneral = hasData([company.assignee, company.created_at]);
  const hasCustomFieldData = hasCustomFields(company.custom_fields);
  
  // Check if custom fields are configured for the organization
  const hasCustomFieldsConfigured = customFieldsConfig.length > 0;
  
  // Determine grid layout based on custom fields presence
  const gridCols = hasCustomFieldsConfigured && hasCustomFieldData ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";

  // Format the address for display
  const formatAddress = () => {
    const parts = [];
    if (company.street) parts.push(company.street);
    if (company.postal_code && company.city) {
      parts.push(`${company.postal_code} ${company.city}`);
    } else if (company.city) {
      parts.push(company.city);
    }
    if (company.country) parts.push(company.country);
    return parts.length > 0 ? parts.join(', ') : '';
  };

  const address = formatAddress();

  return (
    <div className="space-y-8">
      {/* Header Section - Company Name prominently displayed */}
      <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
        <div className="flex flex-col space-y-4">
          {/* Company Name and Logo */}
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 bg-gray-100 dark:bg-gray-800 rounded-sm flex items-center justify-center">
              {company.logo_url ? (
                <img 
                  src={company.logo_url} 
                  alt={`${company.name} logo`} 
                  className="h-12 w-12 object-contain" 
                />
              ) : (
                <Building2 className="h-8 w-8 text-gray-500 dark:text-gray-400" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                {company.name}
                <CompanyRelationshipBadge relationship={company.relationship} size="sm" />
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Dynamic Grid Layout */}
      <div className={`grid ${gridCols} gap-6`}>
        {/* Column 1: Details Information */}
        <div className="space-y-4">
          {hasDetails && (
            <div className="bg-white dark:bg-gray-800 rounded-sm p-4 border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold flex items-center mb-3 text-gray-900 dark:text-gray-100">
                <MapPin className="h-4 w-4 text-blue-500 mr-2" />
                {t("companies.overview.details", "Details")}
              </h3>
              <div className="space-y-3">
                {address && (
                  <div>
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 block flex items-center gap-2">
                      <MapPin className="h-3 w-3" />
                      {t("companies.info.address", "Address")}
                    </span>
                    <a 
                      href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-500 hover:underline"
                    >
                      {address}
                    </a>
                  </div>
                )}
                
                {company.vat_number && (
                  <div>
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 block flex items-center gap-2">
                      <FileText className="h-3 w-3" />
                      {t("companies.createModal.vatNumberLabel", "VAT Number")}
                    </span>
                    <span className="text-sm text-gray-900 dark:text-gray-100">{company.vat_number}</span>
                  </div>
                )}
                
                {company.website_url && (
                  <div>
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 block flex items-center gap-2">
                      <Globe className="h-3 w-3" />
                      {t("companies.createModal.websiteUrlLabel", "Company URL")}
                    </span>
                    <a 
                      href={formatWebsiteUrl(company.website_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-500 hover:underline"
                    >
                      {getDisplayUrl(company.website_url)}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Column 2: General Information */}
        <div className="space-y-4">
          {hasGeneral && (
            <div className="bg-white dark:bg-gray-800 rounded-sm p-4 border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold flex items-center mb-3 text-gray-900 dark:text-gray-100">
                <Building2 className="h-4 w-4 text-blue-500 mr-2" />
                {t("companies.overview.general", "General")}
              </h3>
              <div className="space-y-3">
                {company.created_at && (
                  <div>
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 block flex items-center gap-2">
                      <Calendar className="h-3 w-3" />
                      {t("companies.overview.created", "Created")}
                    </span>
                    <span className="text-sm text-gray-900 dark:text-gray-100">{formatDate(company.created_at)}</span>
                  </div>
                )}
                
                {company.assignee && (
                  <div>
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 block flex items-center gap-2">
                      <User className="h-3 w-3" />
                      {t("companies.info.assignee", "Assignee")}
                    </span>
                    <span className="text-sm text-gray-900 dark:text-gray-100">
                      {assigneeName || `User ${company.assignee.substring(0, 8)}`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Column 3: Custom Fields (if present) */}
        {hasCustomFieldsConfigured && hasCustomFieldData && (
          <div className="space-y-4">
            <div className="bg-white dark:bg-gray-800 rounded-sm p-4 border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold flex items-center mb-3 text-gray-900 dark:text-gray-100">
                <Tag className="h-4 w-4 text-red-600 mr-2" />
                {t("companies.customFields.title", "Custom Fields")}
              </h3>
              <div className="space-y-3">
                {customFieldsConfig.map((fieldConfig) => {
                  const fieldValue = company.custom_fields?.[fieldConfig.name];

                  // For boolean fields, show even if false. For other fields, skip if empty/falsy
                  if (fieldConfig.type !== 'boolean' && !fieldValue) return null;

                  return (
                    <div key={fieldConfig.name}>
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 block flex items-center gap-2">
                        <Tag className="h-3 w-3" />
                        {fieldConfig.name}
                      </span>
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {formatCustomFieldValue(fieldConfig.name, fieldValue)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}; 